const metadata = {
  openapi: '3.0.3',
  info: {
    title: 'EpiSuite API',
    description:
      "Chemical property prediction and environmental assessment API. Calculates properties like LogKow, Henry's Law constant, biodegradation rates, bioconcentration factors, and more from SMILES or CAS numbers.",
    version: '1.0.0',
    contact: {
      name: 'SRC Inc.',
    },
  },
  servers: [
    {
      url: '/',
      description: 'Current server',
    },
  ],
  paths: {
    '/api': {
      get: {
        summary: 'Get OpenAPI specification',
        description: 'Returns the OpenAPI specification JSON document for this API',
        operationId: 'getOpenApiSpec',
        tags: ['Documentation'],
        responses: {
          200: {
            description: 'OpenAPI specification document',
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  description: 'OpenAPI 3.0 specification document',
                },
              },
            },
          },
        },
      },
    },
    '/api/search': {
      get: {
        summary: 'Search chemicals',
        description: 'Search for chemicals by name or CAS number',
        operationId: 'searchChemicals',
        tags: ['Search'],
        parameters: [
          {
            name: 'query',
            in: 'query',
            required: true,
            description: 'Chemical name or CAS number to search for',
            schema: {
              type: 'string',
            },
            example: 'benzene',
          },
        ],
        responses: {
          200: {
            description: 'List of matching chemicals',
            content: {
              'application/json': {
                schema: {
                  type: 'array',
                  items: {
                    $ref: '#/components/schemas/SearchResult',
                  },
                },
              },
            },
          },
          400: {
            description: 'Bad request',
            content: {
              'text/plain': {
                schema: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
    '/api/draw-chemical': {
      get: {
        summary: 'Draw chemical structure',
        description: 'Get an SVG rendering of a chemical structure from its SMILES notation',
        operationId: 'drawChemical',
        tags: ['Visualization'],
        parameters: [
          {
            name: 'smiles',
            in: 'query',
            required: true,
            description: 'SMILES notation of the chemical',
            schema: {
              type: 'string',
            },
            example: 'c1ccccc1',
          },
        ],
        responses: {
          200: {
            description: 'SVG image of the chemical structure',
            content: {
              'image/svg+xml': {
                schema: {
                  type: 'string',
                },
              },
            },
          },
          400: {
            description: 'Bad request',
            content: {
              'text/plain': {
                schema: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
    '/api/submit': {
      get: {
        summary: 'Run EpiSuite calculations',
        description:
          "Submit a chemical for property prediction calculations. Provide either SMILES or CAS number. Results include LogKow, melting/boiling points, vapor pressure, water solubility, Henry's Law constant, biodegradation rates, bioconcentration factors, atmospheric half-life, hydrolysis, fugacity model, sewage treatment model, dermal permeability, and ECOSAR aquatic toxicity predictions.",
        operationId: 'submitCalculation',
        tags: ['Calculations'],
        parameters: [
          {
            name: 'smiles',
            in: 'query',
            required: false,
            description: 'SMILES notation of the chemical. Either smiles or cas is required.',
            schema: {
              type: 'string',
            },
            example: 'c1ccccc1',
          },
          {
            name: 'cas',
            in: 'query',
            required: false,
            description: 'CAS registry number. Either smiles or cas is required.',
            schema: {
              type: 'string',
            },
            example: '71-43-2',
          },
          {
            name: 'caseNumber',
            in: 'query',
            required: false,
            description: 'Case identifier for tracking purposes',
            schema: {
              type: 'string',
            },
          },
          {
            name: 'modules',
            in: 'query',
            required: false,
            description:
              'Comma-separated list of modules to run. If not specified, all modules are run. Valid values: logKow, mpbpvp, waterSolubilityFromLogKow, waterSolubilityFromWaterNt, henrysLawConstant, logKoa, logKoc, biodegradationRate, hydrocarbonBiodegradationRate, bioconcentration, aerosolAdsorptionFraction, atmosphericHalfLife, hydrolysis, waterVolatilization, sewageTreatmentModel, fugacityModel, dermalPermeability, ecosar, aimanalogs',
            schema: {
              type: 'string',
            },
            example: 'logKow,mpbpvp,ecosar',
          },
          {
            name: 'userLogKow',
            in: 'query',
            required: false,
            description: 'User-provided octanol-water partition coefficient (log Kow) override',
            schema: {
              type: 'number',
            },
            example: 2.13,
          },
          {
            name: 'userMeltingPoint',
            in: 'query',
            required: false,
            description: 'User-provided melting point override (Celsius)',
            schema: {
              type: 'number',
            },
            example: 5.5,
          },
          {
            name: 'userBoilingPoint',
            in: 'query',
            required: false,
            description: 'User-provided boiling point override (Celsius)',
            schema: {
              type: 'number',
            },
            example: 80.1,
          },
          {
            name: 'userVaporPressure',
            in: 'query',
            required: false,
            description: 'User-provided vapor pressure override (mmHg)',
            schema: {
              type: 'number',
            },
            example: 95.2,
          },
          {
            name: 'userWaterSolubility',
            in: 'query',
            required: false,
            description: 'User-provided water solubility override (mg/L)',
            schema: {
              type: 'number',
            },
            example: 1790,
          },
          {
            name: 'userHenrysLawConstant',
            in: 'query',
            required: false,
            description: "User-provided Henry's Law constant override (atm-m3/mol)",
            schema: {
              type: 'number',
            },
            example: 0.00555,
          },
          {
            name: 'userLogKoa',
            in: 'query',
            required: false,
            description: 'User-provided octanol-air partition coefficient (log Koa) override',
            schema: {
              type: 'number',
            },
            example: 5.2,
          },
          {
            name: 'userLogKoc',
            in: 'query',
            required: false,
            description: 'User-provided soil adsorption coefficient (log Koc) override (L/kg)',
            schema: {
              type: 'number',
            },
            example: 1.85,
          },
          {
            name: 'userHydroxylReactionRateConstant',
            in: 'query',
            required: false,
            description: 'User-provided hydroxyl reaction rate constant (cm3/molecule-sec)',
            schema: {
              type: 'number',
            },
            example: 1.23e-12,
          },
          {
            name: 'userAtmosphericHydroxylRadicalConcentration',
            in: 'query',
            required: false,
            description: 'User-provided atmospheric hydroxyl radical concentration (radicals/cm3). Default: 1.5e6',
            schema: {
              type: 'number',
              default: 1500000,
            },
            example: 1500000,
          },
          {
            name: 'userAtmosphericOzoneConcentration',
            in: 'query',
            required: false,
            description: 'User-provided atmospheric ozone concentration (molecules/cm3). Default: 7e11',
            schema: {
              type: 'number',
              default: 700000000000,
            },
            example: 700000000000,
          },
          {
            name: 'userAtmosphericDaylightHours',
            in: 'query',
            required: false,
            description: 'User-provided average daylight hours for atmospheric calculations. Default: 12',
            schema: {
              type: 'number',
              default: 12,
            },
            example: 12,
          },
          {
            name: 'userDermalPermeabilityCoefficient',
            in: 'query',
            required: false,
            description: 'User-provided dermal permeability coefficient override (cm/hr)',
            schema: {
              type: 'number',
            },
            example: 0.001,
          },
          {
            name: 'userBiodegradationRateRemoveMetals',
            in: 'query',
            required: false,
            description: 'Whether to remove metals from biodegradation rate calculation. Default: true',
            schema: {
              type: 'boolean',
              default: true,
            },
            example: true,
          },
          {
            name: 'userStpHalfLifePrimaryClarifier',
            in: 'query',
            required: false,
            description: 'User-provided STP primary clarifier half-life (hours). Default: 10000',
            schema: {
              type: 'number',
              default: 10000,
            },
            example: 10000,
          },
          {
            name: 'userStpHalfLifeAerationVessel',
            in: 'query',
            required: false,
            description: 'User-provided STP aeration vessel half-life (hours). Default: 10000',
            schema: {
              type: 'number',
              default: 10000,
            },
            example: 10000,
          },
          {
            name: 'userStpHalfLifeSettlingTank',
            in: 'query',
            required: false,
            description: 'User-provided STP settling tank half-life (hours). Default: 10000',
            schema: {
              type: 'number',
              default: 10000,
            },
            example: 10000,
          },
          {
            name: 'userFugacityHalfLifeAir',
            in: 'query',
            required: false,
            description:
              'User-provided fugacity model air half-life (hours). Default: 0 (calculated from atmospheric half-life)',
            schema: {
              type: 'number',
              default: 0,
            },
            example: 100,
          },
          {
            name: 'userFugacityHalfLifeWater',
            in: 'query',
            required: false,
            description:
              'User-provided fugacity model water half-life (hours). Default: 0 (calculated from biodegradation)',
            schema: {
              type: 'number',
              default: 0,
            },
            example: 360,
          },
          {
            name: 'userFugacityHalfLifeSoil',
            in: 'query',
            required: false,
            description:
              'User-provided fugacity model soil half-life (hours). Default: 0 (calculated from biodegradation)',
            schema: {
              type: 'number',
              default: 0,
            },
            example: 720,
          },
          {
            name: 'userFugacityHalfLifeSediment',
            in: 'query',
            required: false,
            description:
              'User-provided fugacity model sediment half-life (hours). Default: 0 (calculated from biodegradation)',
            schema: {
              type: 'number',
              default: 0,
            },
            example: 1440,
          },
          {
            name: 'userFugacityEmissionRateAir',
            in: 'query',
            required: false,
            description: 'User-provided fugacity model air emission rate (kg/hour). Default: 1000',
            schema: {
              type: 'number',
              default: 1000,
            },
            example: 1000,
          },
          {
            name: 'userFugacityEmissionRateWater',
            in: 'query',
            required: false,
            description: 'User-provided fugacity model water emission rate (kg/hour). Default: 1000',
            schema: {
              type: 'number',
              default: 1000,
            },
            example: 1000,
          },
          {
            name: 'userFugacityEmissionRateSoil',
            in: 'query',
            required: false,
            description: 'User-provided fugacity model soil emission rate (kg/hour). Default: 1000',
            schema: {
              type: 'number',
              default: 1000,
            },
            example: 1000,
          },
          {
            name: 'userFugacityEmissionRateSediment',
            in: 'query',
            required: false,
            description: 'User-provided fugacity model sediment emission rate (kg/hour). Default: 0',
            schema: {
              type: 'number',
              default: 0,
            },
            example: 0,
          },
          {
            name: 'userFugacityAdvectionTimeAir',
            in: 'query',
            required: false,
            description: 'User-provided fugacity model air advection residence time (hours). Default: 100',
            schema: {
              type: 'number',
              default: 100,
            },
            example: 100,
          },
          {
            name: 'userFugacityAdvectionTimeWater',
            in: 'query',
            required: false,
            description: 'User-provided fugacity model water advection residence time (hours). Default: 1000',
            schema: {
              type: 'number',
              default: 1000,
            },
            example: 1000,
          },
          {
            name: 'userFugacityAdvectionTimeSoil',
            in: 'query',
            required: false,
            description: 'User-provided fugacity model soil advection residence time (hours). Default: 0',
            schema: {
              type: 'number',
              default: 0,
            },
            example: 0,
          },
          {
            name: 'userFugacityAdvectionTimeSediment',
            in: 'query',
            required: false,
            description: 'User-provided fugacity model sediment advection residence time (hours). Default: 50000',
            schema: {
              type: 'number',
              default: 50000,
            },
            example: 50000,
          },
        ],
        responses: {
          200: {
            description: 'Calculation results containing property predictions from all requested modules',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/EpiSuiteResults',
                },
                example: {
                  parameters: {
                    smiles: 'c1ccccc1',
                    cas: '71-43-2',
                    caseNumber: null,
                  },
                  chemicalProperties: {
                    name: 'Benzene',
                    cas: '71-43-2',
                    smiles: 'c1ccccc1',
                    molecularWeight: 78.11,
                    molecularFormula: 'C6H6',
                  },
                  logKow: {
                    estimatedValue: {
                      value: 2.13,
                      units: '',
                      valueType: 'ESTIMATED',
                    },
                    selectedValue: {
                      value: 2.13,
                      valueType: 'ESTIMATED',
                    },
                  },
                },
              },
            },
          },
          400: {
            description: 'Bad request - missing required parameter or invalid input',
            content: {
              'text/plain': {
                schema: {
                  type: 'string',
                },
                example: "Either 'smiles' or 'cas' parameter is required",
              },
            },
          },
        },
      },
    },
    '/api/download': {
      get: {
        summary: 'Download CLI JAR',
        description: 'Download the EpiSuite CLI JAR file for local use',
        operationId: 'downloadJar',
        tags: ['Downloads'],
        responses: {
          200: {
            description: 'JAR file download',
            content: {
              'application/java-archive': {
                schema: {
                  type: 'string',
                  format: 'binary',
                },
              },
            },
          },
          500: {
            description: 'Server error',
            content: {
              'text/plain': {
                schema: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
    '/api/ecosar/test': {
      get: {
        summary: 'ECOSAR test endpoint',
        description: 'Test endpoint to verify ECOSAR routes are working',
        operationId: 'ecosarTest',
        tags: ['ECOSAR'],
        responses: {
          200: {
            description: 'Test message',
            content: {
              'text/plain': {
                schema: {
                  type: 'string',
                },
                example: 'ECOSAR test route works!',
              },
            },
          },
        },
      },
    },
    '/api/ecosar/surfactant/{type}': {
      get: {
        summary: 'ECOSAR surfactant predictions',
        description: 'Calculate aquatic toxicity predictions for anionic, cationic, or amphoteric surfactants',
        operationId: 'ecosarSurfactant',
        tags: ['ECOSAR'],
        parameters: [
          {
            name: 'type',
            in: 'path',
            required: true,
            description: 'Surfactant type',
            schema: {
              type: 'string',
              enum: ['anionic', 'cationic', 'amphoteric'],
            },
          },
          {
            name: 'carbonChainLength',
            in: 'query',
            required: false,
            description: 'Carbon chain length of the surfactant',
            schema: {
              type: 'integer',
              default: 0,
            },
            example: 12,
          },
          {
            name: 'waterSolubility',
            in: 'query',
            required: false,
            description: 'Water solubility in mg/L',
            schema: {
              type: 'number',
              default: 0,
            },
            example: 100,
          },
          {
            name: 'cas',
            in: 'query',
            required: false,
            description: 'CAS registry number',
            schema: {
              type: 'string',
            },
          },
          {
            name: 'chemicalName',
            in: 'query',
            required: false,
            description: 'Chemical name',
            schema: {
              type: 'string',
            },
          },
        ],
        responses: {
          200: {
            description: 'Surfactant toxicity predictions',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/EcosarSurfactantResults',
                },
              },
            },
          },
          400: {
            description: 'Bad request',
            content: {
              'text/plain': {
                schema: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
    '/api/ecosar/surfactant/nonionic': {
      get: {
        summary: 'ECOSAR nonionic surfactant predictions',
        description: 'Calculate aquatic toxicity predictions for nonionic surfactants',
        operationId: 'ecosarNonionicSurfactant',
        tags: ['ECOSAR'],
        parameters: [
          {
            name: 'mainCarbons',
            in: 'query',
            required: false,
            description: 'Number of main chain carbons',
            schema: {
              type: 'integer',
              default: 0,
            },
          },
          {
            name: 'branchedCarbons',
            in: 'query',
            required: false,
            description: 'Number of branched carbons',
            schema: {
              type: 'integer',
              default: 0,
            },
          },
          {
            name: 'branches',
            in: 'query',
            required: false,
            description: 'Number of branches',
            schema: {
              type: 'integer',
              default: 0,
            },
          },
          {
            name: 'propoxyGroups',
            in: 'query',
            required: false,
            description: 'Number of propoxy groups',
            schema: {
              type: 'integer',
              default: 0,
            },
          },
          {
            name: 'ethoxylate',
            in: 'query',
            required: false,
            description: 'Number of ethoxylate groups',
            schema: {
              type: 'integer',
              default: 0,
            },
          },
          {
            name: 'waterSolubility',
            in: 'query',
            required: false,
            description: 'Water solubility in mg/L',
            schema: {
              type: 'number',
              default: 0,
            },
          },
          {
            name: 'logKow',
            in: 'query',
            required: false,
            description: 'Log octanol-water partition coefficient',
            schema: {
              type: 'number',
              default: 0,
            },
          },
          {
            name: 'molecularWeight',
            in: 'query',
            required: false,
            description: 'Molecular weight in g/mol',
            schema: {
              type: 'number',
              default: 0,
            },
          },
          {
            name: 'cas',
            in: 'query',
            required: false,
            description: 'CAS registry number',
            schema: {
              type: 'string',
            },
          },
          {
            name: 'chemicalName',
            in: 'query',
            required: false,
            description: 'Chemical name',
            schema: {
              type: 'string',
            },
          },
          {
            name: 'smiles',
            in: 'query',
            required: false,
            description: 'SMILES notation',
            schema: {
              type: 'string',
            },
          },
        ],
        responses: {
          200: {
            description: 'Nonionic surfactant toxicity predictions',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/EcosarNonionicSurfactantResults',
                },
              },
            },
          },
          400: {
            description: 'Bad request',
            content: {
              'text/plain': {
                schema: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
    '/api/ecosar/polymer/{type}': {
      get: {
        summary: 'ECOSAR polymer predictions',
        description: 'Calculate aquatic toxicity predictions for anionic, amphoteric, or polycationic polymers',
        operationId: 'ecosarPolymer',
        tags: ['ECOSAR'],
        parameters: [
          {
            name: 'type',
            in: 'path',
            required: true,
            description: 'Polymer type',
            schema: {
              type: 'string',
              enum: ['anionic', 'amphoteric', 'polycationic'],
            },
          },
          {
            name: 'amineNitrogens',
            in: 'query',
            required: false,
            description: 'Number of amine nitrogens per 1000 daltons',
            schema: {
              type: 'number',
            },
          },
          {
            name: 'polymerType',
            in: 'query',
            required: false,
            description: 'Polymer backbone type',
            schema: {
              type: 'string',
              enum: ['Carbon', 'Silicon', 'Natural'],
            },
          },
          {
            name: 'cationAnionRatio',
            in: 'query',
            required: false,
            description: 'Cation to anion ratio (CAR) for amphoteric polymers',
            schema: {
              type: 'number',
            },
          },
          {
            name: 'averageMolecularWeight',
            in: 'query',
            required: false,
            description: 'Average molecular weight in daltons',
            schema: {
              type: 'number',
            },
          },
          {
            name: 'mw500Percentage',
            in: 'query',
            required: false,
            description: 'Percentage of polymer with MW < 500 daltons',
            schema: {
              type: 'number',
            },
          },
          {
            name: 'mw1000Percentage',
            in: 'query',
            required: false,
            description: 'Percentage of polymer with MW < 1000 daltons',
            schema: {
              type: 'number',
            },
          },
          {
            name: 'cas',
            in: 'query',
            required: false,
            description: 'CAS registry number',
            schema: {
              type: 'string',
            },
          },
          {
            name: 'chemicalName',
            in: 'query',
            required: false,
            description: 'Chemical name',
            schema: {
              type: 'string',
            },
          },
        ],
        responses: {
          200: {
            description: 'Polymer toxicity predictions',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/EcosarPolymerResults',
                },
              },
            },
          },
          400: {
            description: 'Bad request',
            content: {
              'text/plain': {
                schema: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
    '/api/ecosar/polymer/nonionic': {
      get: {
        summary: 'ECOSAR nonionic polymer predictions',
        description: 'Calculate aquatic toxicity predictions for nonionic polymers',
        operationId: 'ecosarNonionicPolymer',
        tags: ['ECOSAR'],
        parameters: [
          {
            name: 'solubilityType',
            in: 'query',
            required: false,
            description: 'Solubility classification of the polymer',
            schema: {
              type: 'string',
              enum: ['INSOLUBLE', 'DISPERSIBLE', 'SOLUBLE'],
            },
          },
          {
            name: 'averageMolecularWeight',
            in: 'query',
            required: false,
            description: 'Average molecular weight in daltons',
            schema: {
              type: 'number',
            },
          },
          {
            name: 'mw500Percentage',
            in: 'query',
            required: false,
            description: 'Percentage of polymer with MW < 500 daltons',
            schema: {
              type: 'number',
            },
          },
          {
            name: 'mw1000Percentage',
            in: 'query',
            required: false,
            description: 'Percentage of polymer with MW < 1000 daltons',
            schema: {
              type: 'number',
            },
          },
          {
            name: 'cas',
            in: 'query',
            required: false,
            description: 'CAS registry number',
            schema: {
              type: 'string',
            },
          },
          {
            name: 'chemicalName',
            in: 'query',
            required: false,
            description: 'Chemical name',
            schema: {
              type: 'string',
            },
          },
        ],
        responses: {
          200: {
            description: 'Nonionic polymer toxicity predictions',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/EcosarNonionicPolymerResults',
                },
              },
            },
          },
          400: {
            description: 'Bad request',
            content: {
              'text/plain': {
                schema: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
    '/api/ecosar/dye': {
      get: {
        summary: 'ECOSAR cationic dye predictions',
        description: 'Calculate aquatic toxicity predictions for cationic dyes',
        operationId: 'ecosarDye',
        tags: ['ECOSAR'],
        parameters: [
          {
            name: 'ethoxylate',
            in: 'query',
            required: false,
            description: 'Number of ethoxylate groups',
            schema: {
              type: 'integer',
              default: 0,
            },
          },
          {
            name: 'molecularWeight',
            in: 'query',
            required: false,
            description: 'Molecular weight in g/mol',
            schema: {
              type: 'number',
              default: 0,
            },
          },
          {
            name: 'waterSolubility',
            in: 'query',
            required: false,
            description: 'Water solubility in mg/L',
            schema: {
              type: 'number',
              default: 0,
            },
          },
          {
            name: 'cas',
            in: 'query',
            required: false,
            description: 'CAS registry number',
            schema: {
              type: 'string',
            },
          },
          {
            name: 'chemicalName',
            in: 'query',
            required: false,
            description: 'Chemical name',
            schema: {
              type: 'string',
            },
          },
        ],
        responses: {
          200: {
            description: 'Cationic dye toxicity predictions',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/EcosarDyeResults',
                },
              },
            },
          },
          400: {
            description: 'Bad request',
            content: {
              'text/plain': {
                schema: {
                  type: 'string',
                },
              },
            },
          },
        },
      },
    },
  },
  components: {
    schemas: {
      ValueType: {
        type: 'string',
        description: 'The source/type of a value',
        enum: ['NONE', 'ESTIMATED', 'EXPERIMENTAL', 'USER_PROVIDED'],
      },
      Value: {
        type: 'object',
        description: 'A value with units and type information',
        properties: {
          value: {
            oneOf: [{ type: 'number' }, { type: 'boolean' }, { type: 'string' }],
            description: 'The actual value',
          },
          units: {
            type: 'string',
            description: 'Units of measurement (empty string if unitless)',
          },
          valueType: {
            $ref: '#/components/schemas/ValueType',
          },
        },
      },
      EstimatedValue: {
        allOf: [
          { $ref: '#/components/schemas/Value' },
          {
            type: 'object',
            description: 'A model-estimated value with calculation details',
            properties: {
              model: {
                type: 'object',
                description: 'Model calculation details including factors and contributions',
                additionalProperties: true,
              },
            },
          },
        ],
      },
      ExperimentalValue: {
        allOf: [
          { $ref: '#/components/schemas/Value' },
          {
            type: 'object',
            description: 'An experimental/measured value from literature',
            properties: {
              author: {
                type: 'string',
                description: 'Author or source of the experimental data',
              },
              year: {
                type: 'integer',
                description: 'Year of publication',
              },
              order: {
                type: 'integer',
                description: 'Order/priority of this value among multiple experimental values',
              },
            },
          },
        ],
      },
      UserProvidedValue: {
        allOf: [
          { $ref: '#/components/schemas/Value' },
          {
            type: 'object',
            description: 'A user-provided override value',
          },
        ],
      },
      ChemicalProperties: {
        type: 'object',
        description: 'Chemical identification and basic properties',
        properties: {
          name: {
            type: 'string',
            description: 'Common chemical name',
          },
          systematicName: {
            type: 'string',
            description: 'IUPAC systematic name',
          },
          cas: {
            type: 'string',
            description: 'CAS registry number',
          },
          smiles: {
            type: 'string',
            description: 'Canonical SMILES notation',
          },
          molecularWeight: {
            type: 'number',
            description: 'Molecular weight in g/mol',
          },
          molecularFormula: {
            type: 'string',
            description: 'Molecular formula',
          },
          molecularFormulaHtml: {
            type: 'string',
            description: 'Molecular formula with HTML subscripts',
          },
          organic: {
            type: 'boolean',
            description: 'Whether the compound contains carbon',
          },
          organicAcid: {
            type: 'boolean',
            description: 'Whether the compound is an organic acid',
          },
          aminoAcid: {
            type: 'boolean',
            description: 'Whether the compound is an amino acid',
          },
          nonStandardMetal: {
            type: 'boolean',
            description: 'Whether the compound contains non-standard metals (not K, Na, Li, Ca)',
          },
          flags: {
            type: 'string',
            description: 'Special flags or warnings for this chemical',
          },
        },
      },
      EpiSuiteParameters: {
        type: 'object',
        description: 'Input parameters for the calculation',
        properties: {
          cas: {
            type: 'string',
            description: 'Input CAS number',
          },
          smiles: {
            type: 'string',
            description: 'Input SMILES notation',
          },
          caseNumber: {
            type: 'string',
            description: 'Case identifier',
          },
          userLogKow: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userMeltingPoint: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userBoilingPoint: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userVaporPressure: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userWaterSolubility: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userHenrysLawConstant: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userLogKoa: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userLogKoc: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userHydroxylReactionRateConstant: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userDermalPermeabilityCoefficient: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userBiodegradationRateRemoveMetals: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userAtmosphericHydroxylRadicalConcentration: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userAtmosphericOzoneConcentration: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userAtmosphericDaylightHours: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userStpHalfLifePrimaryClarifier: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userStpHalfLifeAerationVessel: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userStpHalfLifeSettlingTank: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityHalfLifeAir: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityHalfLifeWater: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityHalfLifeSoil: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityHalfLifeSediment: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityEmissionRateAir: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityEmissionRateWater: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityEmissionRateSoil: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityEmissionRateSediment: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityAdvectionTimeAir: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityAdvectionTimeWater: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityAdvectionTimeSoil: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          userFugacityAdvectionTimeSediment: {
            $ref: '#/components/schemas/UserProvidedValue',
          },
          modules: {
            type: 'array',
            items: {
              type: 'string',
            },
            description: 'List of modules that were run',
          },
        },
      },
      PropertyResults: {
        type: 'object',
        description: 'Generic module results containing estimated, experimental, and selected values',
        properties: {
          parameters: {
            type: 'object',
            description: 'Module-specific input parameters',
            additionalProperties: true,
          },
          estimatedValue: {
            $ref: '#/components/schemas/EstimatedValue',
          },
          experimentalValues: {
            type: 'array',
            items: {
              $ref: '#/components/schemas/ExperimentalValue',
            },
            description: 'List of experimental values from literature',
          },
          selectedValue: {
            $ref: '#/components/schemas/Value',
          },
        },
      },
      LogKowResults: {
        allOf: [
          { $ref: '#/components/schemas/PropertyResults' },
          {
            type: 'object',
            description: 'LogKow (octanol-water partition coefficient) estimation results',
          },
        ],
      },
      MpResults: {
        allOf: [
          { $ref: '#/components/schemas/PropertyResults' },
          {
            type: 'object',
            description: 'Melting point estimation results (Celsius)',
          },
        ],
      },
      BpResults: {
        allOf: [
          { $ref: '#/components/schemas/PropertyResults' },
          {
            type: 'object',
            description: 'Boiling point estimation results (Celsius)',
          },
        ],
      },
      VpResults: {
        allOf: [
          { $ref: '#/components/schemas/PropertyResults' },
          {
            type: 'object',
            description: 'Vapor pressure estimation results (mmHg)',
          },
        ],
      },
      WaterSolubilityResults: {
        allOf: [
          { $ref: '#/components/schemas/PropertyResults' },
          {
            type: 'object',
            description: 'Water solubility estimation results (mg/L)',
          },
        ],
      },
      HenrysLawConstantResults: {
        allOf: [
          { $ref: '#/components/schemas/PropertyResults' },
          {
            type: 'object',
            description: "Henry's Law constant estimation results (atm-m3/mol)",
          },
        ],
      },
      LogKoaResults: {
        allOf: [
          { $ref: '#/components/schemas/PropertyResults' },
          {
            type: 'object',
            description: 'LogKoa (octanol-air partition coefficient) estimation results',
          },
        ],
      },
      LogKocResults: {
        allOf: [
          { $ref: '#/components/schemas/PropertyResults' },
          {
            type: 'object',
            description: 'LogKoc (soil adsorption coefficient) estimation results (L/kg)',
          },
        ],
      },
      BiodegradationRateResults: {
        type: 'object',
        description: 'Biodegradation rate estimation results from BIOWIN models',
        properties: {
          parameters: {
            type: 'object',
            additionalProperties: true,
          },
          biowin1: {
            type: 'object',
            description: 'Linear biodegradation probability',
          },
          biowin2: {
            type: 'object',
            description: 'Non-linear biodegradation probability',
          },
          biowin3: {
            type: 'object',
            description: 'Expert survey biodegradation (ultimate)',
          },
          biowin4: {
            type: 'object',
            description: 'Expert survey biodegradation (primary)',
          },
          biowin5: {
            type: 'object',
            description: 'MITI linear probability',
          },
          biowin6: {
            type: 'object',
            description: 'MITI non-linear probability',
          },
          biowin7: {
            type: 'object',
            description: 'Anaerobic biodegradation probability',
          },
          readyBiodegradability: {
            type: 'string',
            description: 'Overall ready biodegradability assessment',
          },
        },
      },
      HydrocarbonBiodegradationRateResults: {
        type: 'object',
        description: 'Hydrocarbon biodegradation rate estimation results',
        properties: {
          parameters: {
            type: 'object',
            additionalProperties: true,
          },
        },
        additionalProperties: true,
      },
      BioconcentrationModelResults: {
        type: 'object',
        description: 'Bioconcentration factor (BCF/BAF) estimation results',
        properties: {
          'parameters': {
            type: 'object',
            additionalProperties: true,
          },
          'bcfRegressionEstimate': {
            $ref: '#/components/schemas/EstimatedValue',
          },
          'bcfArnot-GobbasMidTrophicEstimate': {
            $ref: '#/components/schemas/EstimatedValue',
          },
          'bafArnot-GobbasMidTrophicEstimate': {
            $ref: '#/components/schemas/EstimatedValue',
          },
          'experimentalValues': {
            type: 'array',
            items: {
              $ref: '#/components/schemas/ExperimentalValue',
            },
          },
        },
        additionalProperties: true,
      },
      AerosolAdsorptionFractionResults: {
        type: 'object',
        description: 'Aerosol adsorption fraction estimation results',
        properties: {
          parameters: {
            type: 'object',
            additionalProperties: true,
          },
          aerosolAdsorptionFraction: {
            $ref: '#/components/schemas/EstimatedValue',
          },
        },
        additionalProperties: true,
      },
      AtmosphericHalfLifeResults: {
        type: 'object',
        description: 'Atmospheric oxidation half-life estimation results',
        properties: {
          parameters: {
            type: 'object',
            additionalProperties: true,
          },
          hydroxylRadicalHalfLife: {
            $ref: '#/components/schemas/EstimatedValue',
          },
          ozoneReactionHalfLife: {
            $ref: '#/components/schemas/EstimatedValue',
          },
          overallAtmosphericHalfLife: {
            $ref: '#/components/schemas/EstimatedValue',
          },
        },
        additionalProperties: true,
      },
      HydrolysisResults: {
        type: 'object',
        description: 'Hydrolysis half-life estimation results',
        properties: {
          parameters: {
            type: 'object',
            additionalProperties: true,
          },
          acidHydrolysis: {
            type: 'object',
            additionalProperties: true,
          },
          neutralHydrolysis: {
            type: 'object',
            additionalProperties: true,
          },
          baseHydrolysis: {
            type: 'object',
            additionalProperties: true,
          },
          overallHalfLife: {
            $ref: '#/components/schemas/EstimatedValue',
          },
        },
        additionalProperties: true,
      },
      WaterVolatilizationResults: {
        type: 'object',
        description: 'Water volatilization half-life estimation results',
        properties: {
          parameters: {
            type: 'object',
            additionalProperties: true,
          },
          riverHalfLife: {
            $ref: '#/components/schemas/EstimatedValue',
          },
          lakeHalfLife: {
            $ref: '#/components/schemas/EstimatedValue',
          },
        },
        additionalProperties: true,
      },
      SewageTreatmentModelResults: {
        type: 'object',
        description: 'Sewage treatment plant removal estimation results',
        properties: {
          parameters: {
            type: 'object',
            additionalProperties: true,
          },
          totalRemoval: {
            $ref: '#/components/schemas/EstimatedValue',
          },
          biodegradationRemoval: {
            $ref: '#/components/schemas/EstimatedValue',
          },
          sludgeAdsorptionRemoval: {
            $ref: '#/components/schemas/EstimatedValue',
          },
          airStrippingRemoval: {
            $ref: '#/components/schemas/EstimatedValue',
          },
        },
        additionalProperties: true,
      },
      FugacityModelResults: {
        type: 'object',
        description: 'Level III fugacity environmental fate model results',
        properties: {
          parameters: {
            type: 'object',
            additionalProperties: true,
          },
          airMassPercent: {
            type: 'number',
            description: 'Percentage of chemical mass in air compartment',
          },
          waterMassPercent: {
            type: 'number',
            description: 'Percentage of chemical mass in water compartment',
          },
          soilMassPercent: {
            type: 'number',
            description: 'Percentage of chemical mass in soil compartment',
          },
          sedimentMassPercent: {
            type: 'number',
            description: 'Percentage of chemical mass in sediment compartment',
          },
          persistenceTime: {
            $ref: '#/components/schemas/EstimatedValue',
          },
        },
        additionalProperties: true,
      },
      DermalPermeabilityResults: {
        type: 'object',
        description: 'Dermal permeability coefficient estimation results',
        properties: {
          parameters: {
            type: 'object',
            additionalProperties: true,
          },
          estimatedValue: {
            $ref: '#/components/schemas/EstimatedValue',
          },
          selectedValue: {
            $ref: '#/components/schemas/Value',
          },
        },
        additionalProperties: true,
      },
      EcosarResults: {
        type: 'object',
        description: 'ECOSAR aquatic toxicity estimation results',
        properties: {
          parameters: {
            type: 'object',
            additionalProperties: true,
          },
          predictions: {
            type: 'array',
            items: {
              $ref: '#/components/schemas/EcosarPrediction',
            },
            description: 'List of toxicity predictions by chemical class',
          },
          alerts: {
            type: 'array',
            items: {
              type: 'string',
            },
            description: 'Warning messages or alerts',
          },
        },
        additionalProperties: true,
      },
      EpiSuiteResults: {
        type: 'object',
        description: 'Complete EpiSuite calculation results',
        properties: {
          parameters: {
            $ref: '#/components/schemas/EpiSuiteParameters',
          },
          chemicalProperties: {
            $ref: '#/components/schemas/ChemicalProperties',
          },
          logKow: {
            $ref: '#/components/schemas/LogKowResults',
          },
          meltingPoint: {
            $ref: '#/components/schemas/MpResults',
          },
          boilingPoint: {
            $ref: '#/components/schemas/BpResults',
          },
          vaporPressure: {
            $ref: '#/components/schemas/VpResults',
          },
          waterSolubilityFromLogKow: {
            $ref: '#/components/schemas/WaterSolubilityResults',
          },
          waterSolubilityFromWaterNt: {
            $ref: '#/components/schemas/WaterSolubilityResults',
          },
          henrysLawConstant: {
            $ref: '#/components/schemas/HenrysLawConstantResults',
          },
          logKoa: {
            $ref: '#/components/schemas/LogKoaResults',
          },
          biodegradationRate: {
            $ref: '#/components/schemas/BiodegradationRateResults',
          },
          hydrocarbonBiodegradationRate: {
            $ref: '#/components/schemas/HydrocarbonBiodegradationRateResults',
          },
          aerosolAdsorptionFraction: {
            $ref: '#/components/schemas/AerosolAdsorptionFractionResults',
          },
          atmosphericHalfLife: {
            $ref: '#/components/schemas/AtmosphericHalfLifeResults',
          },
          logKoc: {
            $ref: '#/components/schemas/LogKocResults',
          },
          hydrolysis: {
            $ref: '#/components/schemas/HydrolysisResults',
          },
          bioconcentration: {
            $ref: '#/components/schemas/BioconcentrationModelResults',
          },
          waterVolatilization: {
            $ref: '#/components/schemas/WaterVolatilizationResults',
          },
          sewageTreatmentModel: {
            $ref: '#/components/schemas/SewageTreatmentModelResults',
          },
          fugacityModel: {
            $ref: '#/components/schemas/FugacityModelResults',
          },
          dermalPermeability: {
            $ref: '#/components/schemas/DermalPermeabilityResults',
          },
          ecosar: {
            $ref: '#/components/schemas/EcosarResults',
          },
          logKowAnalogs: {
            type: 'array',
            items: {
              type: 'string',
            },
            description: 'List of structural analogs found for LogKow estimation',
          },
          analogs: {
            type: 'array',
            items: {
              type: 'string',
            },
            description: 'List of structural analogs',
          },
        },
      },
      SearchResult: {
        type: 'object',
        description: 'A chemical search result',
        properties: {
          name: {
            type: 'string',
            description: 'Chemical name',
          },
          smiles: {
            type: 'string',
            description: 'SMILES notation',
          },
          cas: {
            type: 'string',
            description: 'CAS registry number',
          },
        },
      },
      EcosarPrediction: {
        type: 'object',
        description: 'A single ECOSAR toxicity prediction',
        properties: {
          organism: {
            type: 'string',
            description: 'Target organism (e.g., Fish, Daphnid, Green Algae)',
          },
          duration: {
            type: 'string',
            description: 'Exposure duration (e.g., 96 hr, 48 hr)',
          },
          endpoint: {
            type: 'string',
            description: 'Toxicity endpoint (e.g., LC50, EC50)',
          },
          concentration: {
            type: 'number',
            description: 'Predicted concentration in mg/L',
          },
          flag: {
            type: 'string',
            description: 'Warning flag if applicable',
          },
        },
      },
      EcosarSurfactantParameters: {
        type: 'object',
        description: 'Input parameters for surfactant calculations',
        properties: {
          carbonChainLength: {
            type: 'integer',
            description: 'Carbon chain length',
          },
          waterSolubility: {
            type: 'number',
            description: 'Water solubility in mg/L',
          },
          cas: {
            type: 'string',
            description: 'CAS registry number',
          },
          chemicalName: {
            type: 'string',
            description: 'Chemical name',
          },
        },
      },
      EcosarSurfactantResults: {
        type: 'object',
        description: 'Results from surfactant toxicity calculations',
        properties: {
          parameters: {
            $ref: '#/components/schemas/EcosarSurfactantParameters',
          },
          surfactantType: {
            type: 'string',
            description: 'Type of surfactant (anionic, cationic, amphoteric)',
          },
          predictions: {
            type: 'array',
            items: {
              $ref: '#/components/schemas/EcosarPrediction',
            },
            description: 'List of toxicity predictions',
          },
          alerts: {
            type: 'array',
            items: {
              type: 'string',
            },
            description: 'Warning messages or alerts',
          },
        },
      },
      EcosarNonionicSurfactantParameters: {
        type: 'object',
        description: 'Input parameters for nonionic surfactant calculations',
        properties: {
          mainCarbons: {
            type: 'integer',
            description: 'Number of main chain carbons',
          },
          branchedCarbons: {
            type: 'integer',
            description: 'Number of branched carbons',
          },
          branches: {
            type: 'integer',
            description: 'Number of branches',
          },
          propoxyGroups: {
            type: 'integer',
            description: 'Number of propoxy groups',
          },
          ethoxylate: {
            type: 'integer',
            description: 'Number of ethoxylate groups',
          },
          waterSolubility: {
            type: 'number',
            description: 'Water solubility in mg/L',
          },
          logKow: {
            type: 'number',
            description: 'Log octanol-water partition coefficient',
          },
          molecularWeight: {
            type: 'number',
            description: 'Molecular weight in g/mol',
          },
          cas: {
            type: 'string',
            description: 'CAS registry number',
          },
          chemicalName: {
            type: 'string',
            description: 'Chemical name',
          },
          smiles: {
            type: 'string',
            description: 'SMILES notation',
          },
        },
      },
      EcosarNonionicSurfactantResults: {
        type: 'object',
        description: 'Results from nonionic surfactant toxicity calculations',
        properties: {
          parameters: {
            $ref: '#/components/schemas/EcosarNonionicSurfactantParameters',
          },
          predictions: {
            type: 'array',
            items: {
              $ref: '#/components/schemas/EcosarPrediction',
            },
            description: 'List of toxicity predictions',
          },
          alerts: {
            type: 'array',
            items: {
              type: 'string',
            },
            description: 'Warning messages or alerts',
          },
        },
      },
      EcosarPolymerParameters: {
        type: 'object',
        description: 'Input parameters for polymer calculations',
        properties: {
          amineNitrogens: {
            type: 'number',
            description: 'Number of amine nitrogens per 1000 daltons',
          },
          polymerType: {
            type: 'string',
            description: 'Polymer backbone type (Carbon, Silicon, Natural)',
          },
          cationAnionRatio: {
            type: 'number',
            description: 'Cation to anion ratio (CAR)',
          },
          averageMolecularWeight: {
            type: 'number',
            description: 'Average molecular weight in daltons',
          },
          mw500Percentage: {
            type: 'number',
            description: 'Percentage of polymer with MW < 500 daltons',
          },
          mw1000Percentage: {
            type: 'number',
            description: 'Percentage of polymer with MW < 1000 daltons',
          },
          cas: {
            type: 'string',
            description: 'CAS registry number',
          },
          chemicalName: {
            type: 'string',
            description: 'Chemical name',
          },
        },
      },
      EcosarPolymerResults: {
        type: 'object',
        description: 'Results from polymer toxicity calculations',
        properties: {
          parameters: {
            $ref: '#/components/schemas/EcosarPolymerParameters',
          },
          polymerType: {
            type: 'string',
            description: 'Type of polymer',
          },
          polymerCheck: {
            type: 'string',
            description: 'Polymer exemption check result (SUCCESS, WARNING_ECOSAR, WARNING_ECOSAR_LOW)',
          },
          predictions: {
            type: 'array',
            items: {
              $ref: '#/components/schemas/EcosarPrediction',
            },
            description: 'List of toxicity predictions',
          },
          alerts: {
            type: 'array',
            items: {
              type: 'string',
            },
            description: 'Warning messages or alerts',
          },
        },
      },
      EcosarNonionicPolymerParameters: {
        type: 'object',
        description: 'Input parameters for nonionic polymer calculations',
        properties: {
          solubilityType: {
            type: 'string',
            description: 'Solubility classification (INSOLUBLE, DISPERSIBLE, SOLUBLE)',
          },
          averageMolecularWeight: {
            type: 'number',
            description: 'Average molecular weight in daltons',
          },
          mw500Percentage: {
            type: 'number',
            description: 'Percentage of polymer with MW < 500 daltons',
          },
          mw1000Percentage: {
            type: 'number',
            description: 'Percentage of polymer with MW < 1000 daltons',
          },
          cas: {
            type: 'string',
            description: 'CAS registry number',
          },
          chemicalName: {
            type: 'string',
            description: 'Chemical name',
          },
        },
      },
      EcosarNonionicPolymerResults: {
        type: 'object',
        description: 'Results from nonionic polymer toxicity calculations',
        properties: {
          parameters: {
            $ref: '#/components/schemas/EcosarNonionicPolymerParameters',
          },
          polymerCheck: {
            type: 'string',
            description: 'Polymer exemption check result (SUCCESS, WARNING_ECOSAR, WARNING_ECOSAR_LOW)',
          },
          predictions: {
            type: 'array',
            items: {
              $ref: '#/components/schemas/EcosarPrediction',
            },
            description: 'List of toxicity predictions',
          },
          alerts: {
            type: 'array',
            items: {
              type: 'string',
            },
            description: 'Warning messages or alerts',
          },
        },
      },
      EcosarDyeParameters: {
        type: 'object',
        description: 'Input parameters for cationic dye calculations',
        properties: {
          ethoxylate: {
            type: 'integer',
            description: 'Number of ethoxylate groups',
          },
          molecularWeight: {
            type: 'number',
            description: 'Molecular weight in g/mol',
          },
          waterSolubility: {
            type: 'number',
            description: 'Water solubility in mg/L',
          },
          cas: {
            type: 'string',
            description: 'CAS registry number',
          },
          chemicalName: {
            type: 'string',
            description: 'Chemical name',
          },
        },
      },
      EcosarDyeResults: {
        type: 'object',
        description: 'Results from cationic dye toxicity calculations',
        properties: {
          parameters: {
            $ref: '#/components/schemas/EcosarDyeParameters',
          },
          predictions: {
            type: 'array',
            items: {
              $ref: '#/components/schemas/EcosarPrediction',
            },
            description: 'List of toxicity predictions',
          },
          alerts: {
            type: 'array',
            items: {
              type: 'string',
            },
            description: 'Warning messages or alerts',
          },
        },
      },
    },
  },
  tags: [
    {
      name: 'Documentation',
      description: 'API documentation',
    },
    {
      name: 'Search',
      description: 'Chemical search operations',
    },
    {
      name: 'Visualization',
      description: 'Chemical structure visualization',
    },
    {
      name: 'Calculations',
      description: 'EpiSuite property calculations',
    },
    {
      name: 'Downloads',
      description: 'File downloads',
    },
    {
      name: 'ECOSAR',
      description:
        'Ecological Structure Activity Relationships - aquatic toxicity predictions for surfactants, polymers, and dyes',
    },
  ],
}

export { metadata }
